
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Tag, Clock, Award, Briefcase } from 'lucide-react';
import { Event } from '@/lib/types';
import { getEventDateRangeString } from '@/lib/eventHelpers';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { loadProfessions } from '@/services/professionService';
import { eventTypeLabels } from '@/lib/eventLabels';
import { differenceInDays } from 'date-fns';
import StatusBadge from '@/components/admin/StatusBadge';
import { EventStatus } from '@/services/eventService';
import { useEventTracking } from '@/hooks/useEventTracking';

interface EventCardProps {
  event: Event;
  variant?: 'default' | 'small' | 'featured';
  onClick?: () => void;
}

const EventCard = ({ event, variant = 'default', onClick }: EventCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { trackEventView, trackEventClick } = useEventTracking();

  const isFeatured = variant === 'featured';
  const isSmall = variant === 'small';

  // Make sure we're handling timeNotSpecified correctly by using the proper date strings
  const formattedDateRange = getEventDateRangeString(event.dateStart, event.dateEnd);

  const eventDuration = differenceInDays(new Date(event.dateEnd), new Date(event.dateStart)) + 1;
  const isMultiDay = eventDuration > 1;

  const eventIdentifier = event.slug || event.code;
  const eventName = event.name ? encodeURIComponent(event.name.toLowerCase().replace(/\s+/g, '-')) : '';
  const eventUrl = `/event/${eventIdentifier}${eventName ? '-' + eventName : ''}`;

  const { data: professions = [] } = useQuery({
    queryKey: ['professions'],
    queryFn: loadProfessions,
  });

  const getProfessionName = (id: string) => {
    const profession = professions.find(p => p.id === id);
    return profession ? profession.name : id;
  };

  const getBackgroundColor = () => {
    const types = {
      'conference': 'bg-gray-100 border-gray-200',
      'webinar': 'bg-blue-100 border-blue-200',
      'workshop': 'bg-green-100 border-green-200',
      'training': 'bg-yellow-100 border-yellow-200',
      'meetup': 'bg-pink-100 border-pink-200'
    };

    return types[event.type as keyof typeof types] || 'bg-gray-100 border-gray-200';
  };

  // Track event view automatically when component mounts
  useEffect(() => {
    const eventId = event.id || event.code || event['@id'];
    // Only track if eventId looks like a valid UUID (not a URL path)
    if (eventId && !String(eventId).includes('/') && String(eventId).length > 20) {
      console.log('EventCard mounted, tracking view for event:', eventId);
      trackEventView(String(eventId));
    } else {
      console.log('Skipping tracking for non-UUID eventId:', eventId);
    }
  }, [event, trackEventView]);

  const handleCardClick = (e: React.MouseEvent) => {
    // Track event click - only for valid UUIDs
    const eventId = event.id || event.code || event['@id'];
    if (eventId && !String(eventId).includes('/') && String(eventId).length > 20) {
      trackEventClick(String(eventId), 'card_click');
    }
    
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Link 
      to={eventUrl}
      onClick={handleCardClick}
      className={cn(
        'group relative overflow-hidden bg-white rounded-xl transition-all duration-500 border block',
        isFeatured ? 'shadow-lg border-accent/20' : 'border-gray-100',
        isHovered ? 'shadow-xl transform -translate-y-1' : ''
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col h-full">
        <div className={cn(
          'p-4 flex justify-between items-start border-b',
          isFeatured ? 'bg-accent text-white' : getBackgroundColor()
        )}>
          <div className="flex flex-col">
            <div className={cn(
              'font-bold mb-1',
              isFeatured ? 'text-white' : 'text-gray-700'
            )}>
              {formattedDateRange}
            </div>

            {isMultiDay && (
              <div className={cn(
                'text-xs flex items-center gap-1 mt-1',
                isFeatured ? 'text-white/90' : 'text-gray-600',
                'bg-black/10 rounded-full px-2 py-0.5 w-fit'
              )}>
                <Clock className="w-3 h-3" />
                {eventDuration} nap
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 items-end">
            {event.type && <div className={cn(
              'px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0',
              isFeatured ? 'bg-white text-accent' : 'bg-accent/10 text-accent'
            )}>
              {eventTypeLabels[event.type as keyof typeof eventTypeLabels] || event.type}
            </div>}
            
            {event.status && (
              <StatusBadge 
                status={event.status as EventStatus} 
                showIcon={!isFeatured && !isSmall}
                className={cn(
                  'text-xs',
                  isFeatured ? 'bg-white/20 text-white border-white/30' : ''
                )}
              />
            )}
          </div>
        </div>

        <div className="p-4 flex-grow overflow-hidden">
          <h3 className={cn(
            'font-bold line-clamp-2 transition-colors',
            isFeatured ? 'text-xl' : isSmall ? 'text-base' : 'text-lg',
            'text-gray-900 group-hover:text-accent'
          )}>
            {event.name}
          </h3>

          <p className="mt-2 line-clamp-2 text-sm text-gray-500 overflow-hidden">
            {event.description}
          </p>

          <div className="mt-4 text-sm space-y-2 text-gray-600">
            {event.organizer?.name && (
              <div className="flex items-center overflow-hidden">
                <Briefcase className="w-4 h-4 mr-2 flex-shrink-0 text-accent" />
                <span className="truncate">
                  {event.organizer.name}
                </span>
              </div>
            )}

            <div className="flex items-center overflow-hidden">
              <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-accent" />
              {event.venue && <span className="truncate">
                {event.venue.online ? 'Online' : `${event.venue.city}, ${event.venue.name}`}
              </span>}
            </div>

            {event.maxAttendees && (
              <div className="flex items-center overflow-hidden">
                <Users className="w-4 h-4 mr-2 flex-shrink-0 text-accent" />
                <span className="truncate">
                  Tervezett létszám: {event.maxAttendees} fő
                </span>
              </div>
            )}

            {((event.creditPoints !== undefined && event.creditPoints > 0) || event.postAccredited) && (
              <div className="flex items-center overflow-hidden">
                <Award className="w-4 h-4 mr-2 flex-shrink-0 text-blue-500" />
                <span className="truncate">
                  {event.creditPoints !== undefined && event.creditPoints > 0
                    ? `${event.creditPoints} kreditpont`
                    : "Utóakkreditált"}
                </span>
              </div>
            )}

            {!isSmall && event.professions && event.professions.length > 0 && (
              <div className="flex items-center overflow-hidden">
                <Tag className="w-4 h-4 mr-2 flex-shrink-0 text-accent" />
                <span className="truncate">
                  {event.professions.slice(0, 2).map(id => getProfessionName(id)).join(', ')}
                </span>
              </div>
            )}

            {event.isFeatured && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">
                  <Tag className="w-3 h-3 mr-1" />
                  Kiemelt esemény
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
